// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
export const GRID_STEP_M = 0.2;
export const BRIDGE_DISTANCE_M = 0.6;
export const PERIMETER_DISTANCE_M = 1.5;
export let lastConnectorDebugSkips = [];
// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------
/**
 * Builds a 2D node grid (IRoutingGraph) covering all selectedRooms polygons.
 *
 * For each orientation group (from computeRoomGridOrientations):
 *  1. Project room polygons into the local coordinate frame aligned with that
 *     orientation.
 *  2. Trace scanlines and fill gaps inside the local frame.
 *  3. Transform node positions back to world coordinates when emitting the graph.
 *
 * Rooms of different orientations produce independent grid fragments.
 * Cross-orientation bridge connections are handled in a later pass (TODO).
 */
export function buildNodeGrid(args, gridStepM = GRID_STEP_M, maxCrossOrientationHits = 3, corridorRoomNumbers = new Set()) {
    const { projectNo, floorName, selectedRooms, pipeElevationsM } = args;
    const polygons = selectedRooms.map((r) => ({
        polygon: r.polygon,
        bbox: polygonBBox(r.polygon),
        roomId: r.roomNumber,
    }));
    if (polygons.length === 0) {
        return { graph: { projectNo, systemCode: "AUTO_PIPE_ROUTING", floorName, nodes: [], edges: [] }, corridorAxisNodeIds: new Set() };
    }
    const roomOrientations = computeRoomGridOrientations(polygons);
    const orientationGroups = groupByOrientation(polygons, roomOrientations);
    // Map each roomId → its orientation group index for fast lookup.
    const roomToOrientIdx = new Map();
    for (let i = 0; i < orientationGroups.length; i++) {
        for (const poly of orientationGroups[i].polygonSubset) {
            roomToOrientIdx.set(poly.roomId, i);
        }
    }
    // One connector registry per orientation group — shared/mutated by the connector pass.
    const connectorRegistries = orientationGroups.map(() => new Map());
    const allNodes = [];
    const allEdges = [];
    /** Grid metadata retained for the connector splicing pass. */
    const fragmentMeta = [];
    const allCorridorAxisNodeIds = new Set();
    for (let i = 0; i < orientationGroups.length; i++) {
        const { orientation, polygonSubset } = orientationGroups[i];
        const { nodes, edges, nodeIdGrid, localBBox, orientation: orient, corridorAxisNodeIds } = buildOrientationGridFragment(polygonSubset, polygons, orientation, i, pipeElevationsM, gridStepM, corridorRoomNumbers);
        allNodes.push(...nodes);
        allEdges.push(...edges);
        fragmentMeta.push({ nodeIdGrid, localBBox, orientation: orient });
        for (const id of corridorAxisNodeIds)
            allCorridorAxisNodeIds.add(id);
    }
    // Cross-orientation connector pass: chain nodes along each column trace where
    // an orientation-A room exits and an orientation-B room is within bridge distance.
    const connectorInput = orientationGroups.map(({ orientation, polygonSubset }, i) => ({
        orientation,
        orientIdx: i,
        worldPolygons: polygonSubset,
        orientationRoomIds: new Set(polygonSubset.map((p) => p.roomId)),
    }));
    const { nodes: connNodes, edges: connEdges, debugSkips: connectorDebugSkips } = buildCrossOrientationConnectors(connectorInput, polygons, roomToOrientIdx, connectorRegistries, fragmentMeta, pipeElevationsM, gridStepM, maxCrossOrientationHits);
    allNodes.push(...connNodes);
    allEdges.push(...connEdges);
    lastConnectorDebugSkips = connectorDebugSkips;
    const splicedEdges = spliceCrossOrientationConnectors(allEdges, connectorRegistries, fragmentMeta, pipeElevationsM, gridStepM);
    return {
        graph: {
            projectNo,
            systemCode: "AUTO_PIPE_ROUTING",
            systemName: "Auto pipe routing grid",
            floorName,
            roomNumber: selectedRooms[0]?.roomNumber,
            nodes: allNodes,
            edges: splicedEdges,
        },
        corridorAxisNodeIds: allCorridorAxisNodeIds,
    };
}
// ---------------------------------------------------------------------------
// Per-orientation grid fragment
// ---------------------------------------------------------------------------
/**
 * Groups polygons by identical orientation vector and returns one entry per group.
 */
function groupByOrientation(polygons, roomOrientations) {
    const groups = new Map();
    for (const poly of polygons) {
        const orient = roomOrientations.get(poly.roomId) ?? { dx: 1, dy: 0 };
        // Fixed-precision string key groups bit-identical directions together.
        const key = `${orient.dx.toFixed(9)}_${orient.dy.toFixed(9)}`;
        if (!groups.has(key))
            groups.set(key, { orientation: orient, polygonSubset: [] });
        groups.get(key).polygonSubset.push(poly);
    }
    return [...groups.values()];
}
/**
 * Builds one grid fragment for a single orientation group.
 *
 * Steps:
 *  1. Project all polygon vertices into the local frame (dx,dy) / (-dy,dx).
 *  2. Run column scanlines in local coordinates.
 *  3. Fill horizontal gaps between same-orientation rooms.
 *  4. Emit nodes and edges; transform local positions back to world space.
 */
/**
 * Finds the longest contiguous corridor run along each local axis (row and
 * column) in a single orientation fragment's grid, and returns the node IDs
 * on those two winning lines.
 *
 * Works in local grid indices so the result is always aligned with the room's
 * own coordinate frame.  Gap tolerance is BRIDGE_DISTANCE_M — the same limit
 * used for wall bridging — so adjacent corridor rooms separated by a thin wall
 * are treated as one continuous run.
 */
function findCorridorAxisNodesInFragment(nodeIdGrid, gridCols, gridRows, corridorRoomNumbers, gridStepM) {
    const result = new Set();
    if (corridorRoomNumbers.size === 0)
        return result;
    const maxGapCells = Math.ceil(BRIDGE_DISTANCE_M / gridStepM);
    const isCorridor = (col, row) => (nodeIdGrid[col]?.[row]?.rooms ?? []).some((r) => corridorRoomNumbers.has(r));
    const collectNodes = (col, row, out) => {
        const cell = nodeIdGrid[col]?.[row];
        if (cell?.x)
            out.push(cell.x);
        if (cell?.y)
            out.push(cell.y);
    };
    // Scan along one axis, tracking the longest contiguous corridor run with
    // gap tolerance.  `outer` is the fixed axis; `inner` varies.
    const longestRun = (outerCount, innerCount, isCorr, collect) => {
        let bestLen = 0;
        let bestNodes = [];
        for (let o = 0; o < outerCount; o++) {
            let runStart = -1, lastCorrInner = -1;
            const commitRun = (end) => {
                const len = (end - runStart) * gridStepM;
                if (len > bestLen) {
                    bestLen = len;
                    bestNodes = [];
                    for (let i = runStart; i <= end; i++)
                        collect(o, i, bestNodes);
                }
            };
            for (let i = 0; i < innerCount; i++) {
                if (isCorr(o, i)) {
                    if (runStart === -1)
                        runStart = i;
                    lastCorrInner = i;
                }
                else if (runStart !== -1 && i - lastCorrInner > maxGapCells) {
                    commitRun(lastCorrInner);
                    runStart = -1;
                    lastCorrInner = -1;
                }
            }
            if (runStart !== -1)
                commitRun(lastCorrInner);
        }
        return bestNodes;
    };
    // Best run along local X axis (fixed row, vary col).
    const bestRow = longestRun(gridRows, gridCols, (row, col) => isCorridor(col, row), (row, col, out) => collectNodes(col, row, out));
    // Best run along local Y axis (fixed col, vary row).
    const bestCol = longestRun(gridCols, gridRows, (col, row) => isCorridor(col, row), (col, row, out) => collectNodes(col, row, out));
    for (const id of bestRow)
        result.add(id);
    for (const id of bestCol)
        result.add(id);
    return result;
}
function buildOrientationGridFragment(polygons, allPolygons, orientation, orientIdx, pipeElevationsM, gridStepM, corridorRoomNumbers) {
    const { dx, dy } = orientation;
    // Project polygon vertices into the local coordinate frame.
    const localPolygons = polygons.map(({ polygon, roomId }) => {
        const localPoly = polygon.map(([wx, wy]) => worldToLocal(wx, wy, dx, dy));
        return { polygon: localPoly, bbox: polygonBBox(localPoly), roomId };
    });
    // Project all polygons to local coords so obstructor check works in the same space.
    const allLocalPolygons = allPolygons.map(({ polygon, roomId }) => {
        const localPoly = polygon.map(([wx, wy]) => worldToLocal(wx, wy, dx, dy));
        return { polygon: localPoly, bbox: polygonBBox(localPoly), roomId };
    });
    const localBBox = unionBBox(localPolygons.map((p) => p.bbox));
    const gridCols = Math.ceil((localBBox.maxX - localBBox.minX) / gridStepM) + 1;
    const gridRows = Math.ceil((localBBox.maxY - localBBox.minY) / gridStepM) + 1;
    const orientationRoomIds = new Set(localPolygons.map((p) => p.roomId));
    const obstructorPolygons = allLocalPolygons.filter((p) => !orientationRoomIds.has(p.roomId));
    const keepGrid = buildColumnScanlines(localPolygons, orientationRoomIds, localBBox, gridCols, gridRows, gridStepM);
    const xBridges = findRowBridges(keepGrid, gridCols, gridRows, orientationRoomIds, obstructorPolygons, localBBox, gridStepM);
    const yBridges = findColumnBridges(keepGrid, gridCols, gridRows, orientationRoomIds, obstructorPolygons, localBBox, gridStepM);
    // Perimeter marking temporarily disabled — needs rework for multi-orientation grids.
    const prefix = `o${orientIdx}`;
    const nodeIdGrid = makeNodeIdGrid(keepGrid, gridCols, gridRows, prefix);
    const nodes = buildNodesLocal(nodeIdGrid, gridCols, gridRows, localBBox, orientation, pipeElevationsM, gridStepM);
    const edges = buildEdges(nodeIdGrid, gridCols, gridRows, prefix, xBridges, yBridges);
    const corridorAxisNodeIds = findCorridorAxisNodesInFragment(nodeIdGrid, gridCols, gridRows, corridorRoomNumbers, gridStepM);
    return { nodes, edges, nodeIdGrid, localBBox, orientation, corridorAxisNodeIds };
}
// ---------------------------------------------------------------------------
// Step 2 – column scanlines (y-axis)
// ---------------------------------------------------------------------------
function buildColumnScanlines(polygons, activeRoomIds, globalBBox, gridCols, gridRows, gridStepM) {
    return Array.from({ length: gridCols }, (_, col) => {
        const colX = globalBBox.minX + col * gridStepM;
        // Only process polygons whose x-extent actually covers this column
        const relevant = polygons.filter((p) => p.bbox.minX <= colX && colX <= p.bbox.maxX);
        const intersections = collectYIntersections(colX, relevant);
        intersections.sort((a, b) => a.coord - b.coord);
        return scanlineInsideArray(intersections, globalBBox.minY, gridRows, activeRoomIds, gridStepM);
    });
}
/**
 * For a fixed x = colX, finds all polygon edges that cross that x value and
 * returns their y-intersection coordinates plus traversal direction.
 *
 * Uses the half-open interval [minEdgeX, maxEdgeX) so shared vertices between
 * consecutive edges are counted exactly once.
 */
function collectYIntersections(colX, polygons) {
    const result = [];
    for (const { polygon, roomId } of polygons) {
        const n = polygon.length;
        for (let i = 0; i < n; i++) {
            const ax = polygon[i][0];
            const ay = polygon[i][1];
            const bx = polygon[(i + 1) % n][0];
            const by = polygon[(i + 1) % n][1];
            if (ax === bx)
                continue; // vertical edge – parallel to the scanline axis
            const minEdgeX = Math.min(ax, bx);
            const maxEdgeX = Math.max(ax, bx);
            // Half-open interval: include left endpoint, exclude right
            if (colX < minEdgeX || colX >= maxEdgeX)
                continue;
            const t = (colX - ax) / (bx - ax);
            result.push({
                coord: ay + t * (by - ay),
                goesPositive: bx > ax, // edge travels in +x direction
                roomId,
            });
        }
    }
    return result;
}
/** Mirror of collectYIntersections for horizontal scanlines (fixed y = rowY). */
function collectXIntersections(rowY, polygons) {
    const result = [];
    for (const { polygon, roomId } of polygons) {
        const n = polygon.length;
        for (let i = 0; i < n; i++) {
            const ax = polygon[i][0];
            const ay = polygon[i][1];
            const bx = polygon[(i + 1) % n][0];
            const by = polygon[(i + 1) % n][1];
            if (ay === by)
                continue; // horizontal edge – parallel to scanline
            const minEdgeY = Math.min(ay, by);
            const maxEdgeY = Math.max(ay, by);
            // Half-open interval: include bottom endpoint, exclude top
            if (rowY < minEdgeY || rowY >= maxEdgeY)
                continue;
            const t = (rowY - ay) / (by - ay);
            result.push({
                coord: ax + t * (bx - ax),
                goesPositive: by > ay,
                roomId,
            });
        }
    }
    return result;
}
/**
 * Walks from minCoord upward, one grid step at a time.
 * Only marks cells inside polygons as kept — gap bridging is handled in a
 * separate post-processing pass (findColumnBridges / findRowBridges).
 */
function scanlineInsideArray(sortedIntersections, minCoord, count, _activeRoomIds, gridStepM) {
    const result = Array.from({ length: count }, () => ({ keep: false, rooms: [], perimeter: false }));
    const roomSet = new Set();
    let nextInt = 0;
    for (let i = 0; i < count; i++) {
        const pos = minCoord + i * gridStepM;
        while (nextInt < sortedIntersections.length && sortedIntersections[nextInt].coord < pos) {
            const { roomId } = sortedIntersections[nextInt];
            if (roomSet.has(roomId))
                roomSet.delete(roomId);
            else
                roomSet.add(roomId);
            nextInt++;
        }
        const inRoom = roomSet.size > 0;
        result[i] = { keep: inRoom, rooms: [...roomSet], perimeter: false };
    }
    return result;
}
/**
 * Returns true if any polygon in `polygons` covers the point (testX, rowY).
 * Uses the standard ray-casting even-odd rule.
 */
function pointInAnyPolygon(polygons, testX, rowY) {
    for (const { polygon, bbox } of polygons) {
        if (bbox.maxX <= testX || bbox.minX > testX)
            continue;
        if (bbox.maxY <= rowY || bbox.minY > rowY)
            continue;
        let inside = false;
        const n = polygon.length;
        for (let i = 0; i < n; i++) {
            const ax = polygon[i][0], ay = polygon[i][1];
            const bx = polygon[(i + 1) % n][0], by = polygon[(i + 1) % n][1];
            if (ay === by)
                continue;
            const minY = Math.min(ay, by), maxY = Math.max(ay, by);
            if (rowY < minY || rowY >= maxY)
                continue;
            const ix = ax + ((rowY - ay) / (by - ay)) * (bx - ax);
            if (ix <= testX)
                inside = !inside;
        }
        if (inside)
            return true;
    }
    return false;
}
/**
 * Scans each row for gaps between same-orientation room cells where the
 * wall-to-wall distance is < BRIDGE_DISTANCE_M.  Returns bridge pairs
 * (colA, colB, row) for gaps that have no differently-oriented room in between.
 * No new grid cells are created; the caller adds direct edges between the
 * existing room-boundary X-nodes.
 */
function findRowBridges(keepGrid, gridCols, gridRows, activeRoomIds, obstructorPolygons, localBBox, gridStepM) {
    const maxGap = Math.ceil(BRIDGE_DISTANCE_M / gridStepM);
    const bridges = [];
    for (let row = 0; row < gridRows; row++) {
        const rowLocalY = localBBox.minY + row * gridStepM;
        let segEndCol = -1;
        for (let col = 0; col < gridCols; col++) {
            const cell = keepGrid[col][row];
            // Only same-orientation room cells are valid bridge endpoints.
            if (cell.keep && cell.rooms.some((id) => activeRoomIds.has(id))) {
                segEndCol = col;
            }
            else if (segEndCol !== -1 && !cell.keep) {
                // Look ahead for the next same-orientation room cell within maxGap steps.
                let nextKeptCol = -1;
                const limit = Math.min(col + maxGap, gridCols - 1);
                for (let k = col + 1; k <= limit; k++) {
                    const kc = keepGrid[k][row];
                    if (kc.keep && kc.rooms.some((id) => activeRoomIds.has(id))) {
                        nextKeptCol = k;
                        break;
                    }
                }
                if (nextKeptCol !== -1) {
                    // All gap cells must be empty (no intermediate room in the gap).
                    const gapClean = (() => {
                        for (let k = segEndCol + 1; k < nextKeptCol; k++) {
                            if ((keepGrid[k][row].rooms.length) > 0)
                                return false;
                        }
                        return true;
                    })();
                    // Check for obstructor (different-orientation) rooms in the gap midpoint.
                    const midLocalX = localBBox.minX + ((segEndCol + nextKeptCol) / 2) * gridStepM;
                    if (gapClean && !pointInAnyPolygon(obstructorPolygons, midLocalX, rowLocalY)) {
                        bridges.push({ colA: segEndCol, colB: nextKeptCol, row });
                    }
                    col = nextKeptCol - 1;
                    segEndCol = nextKeptCol;
                }
                else {
                    segEndCol = -1;
                }
            }
        }
    }
    return bridges;
}
/**
 * Scans each column for gaps between same-orientation room cells where the
 * wall-to-wall distance is < BRIDGE_DISTANCE_M.  Symmetric to findRowBridges.
 * No new grid cells are created; the caller adds direct edges between the
 * existing room-boundary Y-nodes.
 */
function findColumnBridges(keepGrid, gridCols, gridRows, activeRoomIds, obstructorPolygons, localBBox, gridStepM) {
    const maxGap = Math.ceil(BRIDGE_DISTANCE_M / gridStepM);
    const bridges = [];
    for (let col = 0; col < gridCols; col++) {
        const colLocalX = localBBox.minX + col * gridStepM;
        let segEndRow = -1;
        for (let row = 0; row < gridRows; row++) {
            const cell = keepGrid[col][row];
            if (cell.keep && cell.rooms.some((id) => activeRoomIds.has(id))) {
                segEndRow = row;
            }
            else if (segEndRow !== -1 && !cell.keep) {
                let nextKeptRow = -1;
                const limit = Math.min(row + maxGap, gridRows - 1);
                for (let k = row + 1; k <= limit; k++) {
                    const kc = keepGrid[col][k];
                    if (kc && kc.keep && kc.rooms.some((id) => activeRoomIds.has(id))) {
                        nextKeptRow = k;
                        break;
                    }
                }
                if (nextKeptRow !== -1) {
                    // All gap cells must be empty (no intermediate room in the gap).
                    const gapClean = (() => {
                        for (let k = segEndRow + 1; k < nextKeptRow; k++) {
                            if ((keepGrid[col][k].rooms.length) > 0)
                                return false;
                        }
                        return true;
                    })();
                    const midLocalY = localBBox.minY + ((segEndRow + nextKeptRow) / 2) * gridStepM;
                    if (gapClean && !pointInAnyPolygon(obstructorPolygons, colLocalX, midLocalY)) {
                        bridges.push({ col, rowA: segEndRow, rowB: nextKeptRow });
                    }
                    row = nextKeptRow - 1;
                    segEndRow = nextKeptRow;
                }
                else {
                    segEndRow = -1;
                }
            }
        }
    }
    return bridges;
}
function makeNodeIdGrid(keepGrid, gridCols, gridRows, prefix) {
    return Array.from({ length: gridCols }, (_, col) => Array.from({ length: gridRows }, (_, row) => {
        const cell = keepGrid[col][row];
        if (!cell.keep)
            return { x: null, y: null, rooms: [], perimeter: false };
        return {
            x: `gn-x-${prefix}-${col}-${row}`,
            y: `gn-y-${prefix}-${col}-${row}`,
            rooms: cell.rooms,
            perimeter: cell.perimeter,
        };
    }));
}
/**
 * Emits graph nodes for one orientation fragment.
 * Local grid coordinates (col, row) are converted back to world space using
 * the local bounding box origin and the orientation axes (dx, dy) / (-dy, dx).
 */
function buildNodesLocal(nodeIdGrid, gridCols, gridRows, localBBox, orientation, pipeElevationsM, gridStepM) {
    const { dx, dy } = orientation;
    const nodes = [];
    for (let col = 0; col < gridCols; col++) {
        for (let row = 0; row < gridRows; row++) {
            const { x, y, rooms, perimeter } = nodeIdGrid[col][row];
            if (!x && !y)
                continue;
            const localX = localBBox.minX + col * gridStepM;
            const localY = localBBox.minY + row * gridStepM;
            const [posX, posY] = localToWorld(localX, localY, dx, dy);
            const roomsProp = rooms.length > 0 ? rooms : undefined;
            const perimProp = perimeter || undefined;
            if (x)
                nodes.push({ id: x, posM: [posX, posY, pipeElevationsM.xDirection], kind: "JUNCTION", rooms: roomsProp, perimeter: perimProp });
            if (y)
                nodes.push({ id: y, posM: [posX, posY, pipeElevationsM.yDirection], kind: "JUNCTION", rooms: roomsProp, perimeter: perimProp });
        }
    }
    return nodes;
}
function buildEdges(nodeIdGrid, gridCols, gridRows, prefix, xBridges = [], yBridges = []) {
    const edges = [];
    // Riser edges: X-node ↔ Y-node at the same grid position.
    for (let col = 0; col < gridCols; col++) {
        for (let row = 0; row < gridRows; row++) {
            const { x, y } = nodeIdGrid[col][row];
            if (x && y) {
                edges.push({ id: `ge-r-${prefix}-${col}-${row}`, from: x, to: y, compCode: `GRID-RISER-${col}-${row}`, profile: "CIRCULAR", size: { diameterM: 0.1 } });
            }
        }
    }
    // Horizontal edges: X-node to X-node (pipes running along local X axis).
    for (let col = 0; col < gridCols - 1; col++) {
        for (let row = 0; row < gridRows; row++) {
            const from = nodeIdGrid[col][row].x;
            const to = nodeIdGrid[col + 1][row].x;
            if (from && to) {
                edges.push({ id: `ge-h-${prefix}-${col}-${row}`, from, to, compCode: `GRID-H-${col}-${row}`, profile: "CIRCULAR", size: { diameterM: 0.1 } });
            }
        }
    }
    // Vertical edges: Y-node to Y-node (pipes running along local Y axis).
    for (let col = 0; col < gridCols; col++) {
        for (let row = 0; row < gridRows - 1; row++) {
            const from = nodeIdGrid[col][row].y;
            const to = nodeIdGrid[col][row + 1].y;
            if (from && to) {
                edges.push({ id: `ge-v-${prefix}-${col}-${row}`, from, to, compCode: `GRID-V-${col}-${row}`, profile: "CIRCULAR", size: { diameterM: 0.1 } });
            }
        }
    }
    // Long-range X-bridge edges: direct connection between existing room-boundary
    // X-nodes across a horizontal gap (no intermediate nodes created).
    for (const { colA, colB, row } of xBridges) {
        const from = nodeIdGrid[colA]?.[row]?.x;
        const to = nodeIdGrid[colB]?.[row]?.x;
        if (from && to) {
            edges.push({ id: `ge-xb-${prefix}-${colA}-${colB}-${row}`, from, to, compCode: `GRID-XB-${colA}-${colB}-${row}`, profile: "CIRCULAR", size: { diameterM: 0.1 } });
        }
    }
    // Long-range Y-bridge edges: direct connection between existing room-boundary
    // Y-nodes across a vertical gap (no intermediate nodes created).
    for (const { col, rowA, rowB } of yBridges) {
        const from = nodeIdGrid[col]?.[rowA]?.y;
        const to = nodeIdGrid[col]?.[rowB]?.y;
        if (from && to) {
            edges.push({ id: `ge-yb-${prefix}-${col}-${rowA}-${rowB}`, from, to, compCode: `GRID-YB-${col}-${rowA}-${rowB}`, profile: "CIRCULAR", size: { diameterM: 0.1 } });
        }
    }
    return edges;
}
// ---------------------------------------------------------------------------
// Room grid orientation assignment
// ---------------------------------------------------------------------------
const NUM_ORIENTATION_BINS = 16; // π/32 rad resolution → 16 bins covering [0, π/2)
const ORIENTATION_BIN_ANGLE = Math.PI / 32; // width of each bin
/**
 * Rotates (dx, dy) by successive 90° CCW steps (x,y → -y,x) until both
 * components are ≥ 0, mapping any direction into the first quadrant [0, π/2].
 */
function normalizeToQ1(dx, dy) {
    let x = dx, y = dy;
    for (let i = 0; i < 4; i++) {
        if (x >= 0 && y >= 0)
            return { x, y };
        const nx = -y, ny = x;
        x = nx;
        y = ny;
    }
    return { x, y };
}
/** Maps a direction vector to a bin index in [0, NUM_ORIENTATION_BINS-1]. */
function directionToBinIdx(dx, dy) {
    const { x, y } = normalizeToQ1(dx, dy);
    return Math.min(NUM_ORIENTATION_BINS - 1, Math.floor(Math.atan2(y, x) / ORIENTATION_BIN_ANGLE));
}
function emptyOrientationBins() {
    return Array.from({ length: NUM_ORIENTATION_BINS }, () => ({
        sumX: 0, sumY: 0, totalLength: 0, roomLengths: new Map(),
    }));
}
/** Accumulates all wall segments of `activeIds` rooms into `bins`. */
function accumulateIntoBins(polygons, activeIds, bins) {
    for (const { polygon, roomId } of polygons) {
        if (!activeIds.has(roomId))
            continue;
        const n = polygon.length;
        for (let i = 0; i < n; i++) {
            const [ax, ay] = polygon[i];
            const [bx, by] = polygon[(i + 1) % n];
            const dx = bx - ax, dy = by - ay;
            const len = Math.sqrt(dx * dx + dy * dy);
            if (len < 1e-9)
                continue;
            // Rotate to Q1; length is preserved by rotation.
            const { x, y } = normalizeToQ1(dx, dy);
            const binIdx = directionToBinIdx(dx, dy);
            const bin = bins[binIdx];
            bin.sumX += x;
            bin.sumY += y;
            bin.totalLength += len;
            bin.roomLengths.set(roomId, (bin.roomLengths.get(roomId) ?? 0) + len);
        }
    }
}
/**
 * Returns the index of the first bin in the adjacent pair (i, i+1 mod N) whose
 * combined total segment length is greatest.  Bins 15 and 0 are also considered
 * adjacent (wrap-around).
 */
function findBestAdjacentPair(bins) {
    let bestIdx = 0, bestLen = -1;
    for (let i = 0; i < NUM_ORIENTATION_BINS; i++) {
        const total = bins[i].totalLength + bins[(i + 1) % NUM_ORIENTATION_BINS].totalLength;
        if (total > bestLen) {
            bestLen = total;
            bestIdx = i;
        }
    }
    return bestIdx;
}
/**
 * Computes the dominant grid direction (unit vector in Q1) from the pair
 * (binA, binA+1 mod N).
 *
 * For the wrap-around pair (15, 0): bin 0's accumulated vector is rotated 90°
 * CCW before summing with bin 15's vector so that both contributions point in
 * the same angular neighbourhood.  The combined sum is then normalised to Q1.
 */
function directionFromPair(bins, binA) {
    const binB = (binA + 1) % NUM_ORIENTATION_BINS;
    const isWrap = binA === NUM_ORIENTATION_BINS - 1; // pair (15, 0)
    let totalX = bins[binA].sumX;
    let totalY = bins[binA].sumY;
    if (isWrap) {
        // Rotate bin 0 by 90° CCW to align with bin 15: (x,y) → (-y, x)
        totalX += -bins[binB].sumY;
        totalY += bins[binB].sumX;
    }
    else {
        totalX += bins[binB].sumX;
        totalY += bins[binB].sumY;
    }
    const { x, y } = normalizeToQ1(totalX, totalY);
    const len = Math.sqrt(x * x + y * y);
    if (len < 1e-9)
        return { dx: 1, dy: 0 }; // fallback: axis-aligned
    return { dx: x / len, dy: y / len };
}
/**
 * Iteratively assigns a grid orientation to every room.
 *
 * Each iteration:
 *  1. Bin all remaining rooms' wall segments by direction (Q1-normalised).
 *  2. Find the two adjacent bins with the greatest combined wall length.
 *  3. Compute the dominant direction for those bins.
 *  4. Assign that orientation to rooms whose dominant bin (highest per-room
 *     length) falls in the selected pair.
 *  5. Remove assigned rooms; repeat until none remain.
 *
 * Returns a map from roomId → unit direction vector (dx, dy) in Q1.
 */
export function computeRoomGridOrientations(polygons) {
    const result = new Map();
    const remaining = new Set(polygons.map((p) => p.roomId));
    while (remaining.size > 0) {
        const bins = emptyOrientationBins();
        accumulateIntoBins(polygons, remaining, bins);
        const bestA = findBestAdjacentPair(bins);
        const bestB = (bestA + 1) % NUM_ORIENTATION_BINS;
        const direction = directionFromPair(bins, bestA);
        // Assign rooms whose dominant bin falls in {bestA, bestB}.
        const toAssign = new Set();
        for (const roomId of remaining) {
            let maxLen = 0, domBin = 0;
            for (let i = 0; i < NUM_ORIENTATION_BINS; i++) {
                const len = bins[i].roomLengths.get(roomId) ?? 0;
                if (len > maxLen) {
                    maxLen = len;
                    domBin = i;
                }
            }
            if (domBin === bestA || domBin === bestB)
                toAssign.add(roomId);
        }
        // Fallback: if no room's dominant bin matched, take any room with a
        // contribution to the pair — prevents infinite loops on degenerate input.
        if (toAssign.size === 0) {
            for (const roomId of remaining) {
                const inPair = (bins[bestA].roomLengths.get(roomId) ?? 0)
                    + (bins[bestB].roomLengths.get(roomId) ?? 0);
                if (inPair > 0)
                    toAssign.add(roomId);
            }
        }
        if (toAssign.size === 0) {
            // Last resort: assign everything remaining.
            for (const roomId of remaining)
                toAssign.add(roomId);
        }
        for (const roomId of toAssign) {
            result.set(roomId, direction);
            remaining.delete(roomId);
        }
    }
    return result;
}
const CONN_KEY_PRECISION = 1e-4; // ~0.1 mm — tight enough for float intersection points
function connSpatialKey(wx, wy) {
    return `${Math.round(wx / CONN_KEY_PRECISION)}_${Math.round(wy / CONN_KEY_PRECISION)}`;
}
/** Registry key that includes elevation so same-XY nodes at different heights are distinct. */
function connNodeKey(wx, wy, elevM) {
    return `${connSpatialKey(wx, wy)}_${elevM.toFixed(3)}`;
}
/**
 * For each orientation group A, scans every column of A's local grid.  When a
 * column exits a room of orientation A and the next room belongs to a different
 * orientation B (gap < BRIDGE_DISTANCE_M), it:
 *
 *  1. Calls findOrientationGridIntersections to locate where the trace crosses
 *     the grid lines of orientation B.
 *  2. For intersections inside the gap: looks up / creates a connector vertex
 *     in A's registry (deduplication across columns).
 *  3. For intersections inside room B: creates a connector vertex in B's
 *     registry.
 *  4. Emits edges chaining consecutive connector nodes along the trace.
 *
 * Edges between these connectors and the regular grid nodes are deferred to a
 * separate integration pass.
 */
function buildCrossOrientationConnectors(orientationGroups, allWorldPolygons, roomToOrientIdx, connectorRegistries, fragmentMeta, pipeElevationsM, gridStepM, maxCrossOrientationHits) {
    const nodes = [];
    const edges = [];
    const debugSkips = [];
    let seq = 0;
    for (const { orientation, orientIdx, orientationRoomIds } of orientationGroups) {
        const { dx, dy } = orientation;
        // Project all world polygons into A's local frame for the column scan.
        const allLocal = allWorldPolygons.map(({ polygon, roomId }) => {
            const localPoly = polygon.map(([wx, wy]) => worldToLocal(wx, wy, dx, dy));
            return { polygon: localPoly, bbox: polygonBBox(localPoly), roomId };
        });
        // Column range is determined by A's own rooms only.
        const aLocal = allLocal.filter((p) => orientationRoomIds.has(p.roomId));
        if (aLocal.length === 0)
            continue;
        const localBBox = unionBBox(aLocal.map((p) => p.bbox));
        const gridCols = Math.ceil((localBBox.maxX - localBBox.minX) / gridStepM) + 1;
        const gridRows = Math.ceil((localBBox.maxY - localBBox.minY) / gridStepM) + 1;
        // Scan along both local axes: columns (local-Y trace) and rows (local-X trace).
        for (const scanAxis of ["col", "row"]) {
            const gridCount = scanAxis === "col" ? gridCols : gridRows;
            const scanOrigin = scanAxis === "col" ? localBBox.minX : localBBox.minY;
            const traceElevM = scanAxis === "col" ? pipeElevationsM.yDirection : pipeElevationsM.xDirection;
            const posDir = scanAxis === "col" ? [-dy, dx] : [dx, dy];
            const negDir = scanAxis === "col" ? [dy, -dx] : [-dx, -dy];
            for (let idx = 0; idx < gridCount; idx++) {
                const scanCoord = scanOrigin + idx * gridStepM;
                const relevant = scanAxis === "col"
                    ? allLocal.filter((p) => p.bbox.minX <= scanCoord && scanCoord <= p.bbox.maxX)
                    : allLocal.filter((p) => p.bbox.minY <= scanCoord && scanCoord <= p.bbox.maxY);
                const intersections = scanAxis === "col"
                    ? collectYIntersections(scanCoord, relevant)
                    : collectXIntersections(scanCoord, relevant);
                intersections.sort((a, b) => a.coord - b.coord);
                for (const dir of [1, -1]) {
                    const orderedIntersections = dir === 1 ? intersections : [...intersections].reverse();
                    const traceDirWorld = dir === 1 ? posDir : negDir;
                    const roomSet = new Set();
                    let lastAExitCoord = undefined;
                    let prevConnId = undefined;
                    let pendingNonAEntry = undefined;
                    for (let ii = 0; ii < orderedIntersections.length; ii++) {
                        const isect = orderedIntersections[ii];
                        const wasInA = [...roomSet].some((id) => orientationRoomIds.has(id));
                        if (roomSet.has(isect.roomId))
                            roomSet.delete(isect.roomId);
                        else
                            roomSet.add(isect.roomId);
                        const nowInA = [...roomSet].some((id) => orientationRoomIds.has(id));
                        const nowInNonA = [...roomSet].some((id) => !orientationRoomIds.has(id));
                        // Overlap case: non-A room entered while still in A. Save it as pending so
                        // the bridge fires at the real A-exit rather than here.
                        if (wasInA && nowInA && !orientationRoomIds.has(isect.roomId) && roomSet.has(isect.roomId)) {
                            pendingNonAEntry = { roomId: isect.roomId, entryCoord: isect.coord };
                        }
                        if (wasInA && !nowInA) {
                            lastAExitCoord = isect.coord;
                            prevConnId = undefined;
                        }
                        // If A just exited and there is a pending non-A entry from an overlap,
                        // synthesize the bridge check at this coord (gap treated as 0).
                        const effectiveBEntry = (!wasInA && nowInNonA)
                            ? { roomId: isect.roomId, coord: isect.coord }
                            : (wasInA && !nowInA && pendingNonAEntry)
                                ? { roomId: pendingNonAEntry.roomId, coord: isect.coord }
                                : undefined;
                        if (wasInA && !nowInA)
                            pendingNonAEntry = undefined;
                        if (effectiveBEntry !== undefined && lastAExitCoord !== undefined) {
                            const { roomId: bRoomId, coord: bCoord } = effectiveBEntry;
                            const otherOrientIdx = roomToOrientIdx.get(bRoomId);
                            if (otherOrientIdx === undefined || otherOrientIdx === orientIdx) {
                                debugSkips.push({
                                    reason: otherOrientIdx === undefined ? "roomId_not_in_roomToOrientIdx" : "same_orientation",
                                    orientIdx, scanAxis, idx, dir, coord: bCoord, roomId: bRoomId,
                                });
                                lastAExitCoord = undefined;
                                continue;
                            }
                            const gap = Math.abs(bCoord - lastAExitCoord);
                            if (gap > BRIDGE_DISTANCE_M) {
                                debugSkips.push({
                                    reason: "gap_too_large",
                                    orientIdx, scanAxis, idx, dir, coord: bCoord, roomId: bRoomId,
                                });
                                lastAExitCoord = undefined;
                                continue;
                            }
                            const otherOrientation = orientationGroups.find((g) => g.orientIdx === otherOrientIdx).orientation;
                            let exitBCoord = undefined;
                            for (let k = ii + 1; k < orderedIntersections.length; k++) {
                                if (orderedIntersections[k].roomId === bRoomId) {
                                    exitBCoord = orderedIntersections[k].coord;
                                    break;
                                }
                            }
                            const maxDist = exitBCoord !== undefined
                                ? Math.min(Math.abs(exitBCoord - lastAExitCoord), gap + BRIDGE_DISTANCE_M)
                                : gap + BRIDGE_DISTANCE_M;
                            const exitWorld = scanAxis === "col"
                                ? localToWorld(scanCoord, lastAExitCoord, dx, dy)
                                : localToWorld(lastAExitCoord, scanCoord, dx, dy);
                            const bMeta = fragmentMeta[otherOrientIdx];
                            const gridOrigin = bMeta
                                ? { x: bMeta.localBBox.minX, y: bMeta.localBBox.minY }
                                : { x: 0, y: 0 };
                            const hits = findOrientationGridIntersections(exitWorld, traceDirWorld, otherOrientation, maxDist, maxCrossOrientationHits, gridOrigin, gridStepM);
                            const traceCache = new Map();
                            const aMeta = fragmentMeta[orientIdx];
                            if (aMeta) {
                                let aExitNodeId;
                                if (scanAxis === "col") {
                                    const rowFrac = (lastAExitCoord - aMeta.localBBox.minY) / gridStepM;
                                    const exitRow = dir === 1 ? Math.floor(rowFrac) : Math.ceil(rowFrac);
                                    aExitNodeId = aMeta.nodeIdGrid[idx]?.[exitRow]?.y;
                                }
                                else {
                                    const colFrac = (lastAExitCoord - aMeta.localBBox.minX) / gridStepM;
                                    const exitCol = dir === 1 ? Math.floor(colFrac) : Math.ceil(colFrac);
                                    aExitNodeId = aMeta.nodeIdGrid[exitCol]?.[idx]?.x;
                                }
                                if (aExitNodeId)
                                    prevConnId = aExitNodeId;
                            }
                            for (const hit of hits) {
                                const isInGap = hit.t < gap;
                                const [hx, hy] = hit.point;
                                const hitElevM = hit.axis === "x" ? pipeElevationsM.yDirection : pipeElevationsM.xDirection;
                                const sameHeight = hitElevM === traceElevM;
                                const regKey = connNodeKey(hx, hy, hitElevM);
                                let regCv;
                                if (isInGap) {
                                    regCv = connectorRegistries[orientIdx].get(regKey)
                                        ?? connectorRegistries[otherOrientIdx].get(regKey);
                                    if (!regCv) {
                                        const id = `cn-${otherOrientIdx}-${seq++}`;
                                        regCv = { id, worldPos: [hx, hy], elevM: hitElevM };
                                        connectorRegistries[otherOrientIdx].set(regKey, regCv);
                                        nodes.push({ id, posM: [hx, hy, hitElevM], kind: "JUNCTION" });
                                    }
                                }
                                else {
                                    regCv = connectorRegistries[otherOrientIdx].get(regKey);
                                    if (!regCv) {
                                        const id = `cn-${otherOrientIdx}-${seq++}`;
                                        regCv = { id, worldPos: [hx, hy], elevM: hitElevM };
                                        connectorRegistries[otherOrientIdx].set(regKey, regCv);
                                        nodes.push({ id, posM: [hx, hy, hitElevM], kind: "JUNCTION", rooms: [bRoomId] });
                                    }
                                }
                                let traceId;
                                if (sameHeight) {
                                    traceId = regCv.id;
                                }
                                else {
                                    const traceKey = connNodeKey(hx, hy, traceElevM);
                                    let cachedId = traceCache.get(traceKey);
                                    if (!cachedId) {
                                        cachedId = `cn-${orientIdx}-${seq++}`;
                                        traceCache.set(traceKey, cachedId);
                                        nodes.push({ id: cachedId, posM: [hx, hy, traceElevM], kind: "JUNCTION" });
                                    }
                                    traceId = cachedId;
                                    edges.push({
                                        id: `cr-${seq++}`,
                                        from: traceId,
                                        to: regCv.id,
                                        compCode: "CONNECTOR-RISER",
                                        profile: "CIRCULAR",
                                        size: { diameterM: 0.1 },
                                    });
                                }
                                if (prevConnId && prevConnId !== traceId) {
                                    edges.push({
                                        id: `ce-${orientIdx}-${otherOrientIdx}-${seq++}`,
                                        from: prevConnId,
                                        to: traceId,
                                        compCode: `CONNECTOR-${orientIdx}-${otherOrientIdx}`,
                                        profile: "CIRCULAR",
                                        size: { diameterM: 0.1 },
                                    });
                                }
                                prevConnId = traceId;
                            }
                            lastAExitCoord = undefined;
                        }
                    } // for ii
                } // for dir
            } // for idx
        } // for scanAxis
    }
    return { nodes, edges, debugSkips };
}
// ---------------------------------------------------------------------------
// Connector splicing pass
// ---------------------------------------------------------------------------
/**
 * Splices each connector vertex into the existing edge of its orientation grid
 * that it lies on.
 *
 * For each connector in each orientation's registry:
 *  - elevM === yDirection → it sits on a column line → find the vertical edge
 *    (ge-v-*) between the two Y-nodes bracketing it and split it.
 *  - elevM === xDirection → it sits on a row line → find the horizontal edge
 *    (ge-h-*) between the two X-nodes bracketing it and split it.
 *
 * Multiple connectors on the same edge are sorted by position along the edge
 * before chaining: fromNode → c0 → c1 → … → toNode.
 *
 * Returns a new edge array with the spliced edges removed and the replacement
 * sub-edges inserted.
 */
function spliceCrossOrientationConnectors(allEdges, connectorRegistries, fragmentMeta, pipeElevationsM, gridStepM) {
    // edgeId → list of { connId, t } where t ∈ (0,1) is position along the edge.
    const spliceMap = new Map();
    for (let orientIdx = 0; orientIdx < connectorRegistries.length; orientIdx++) {
        const registry = connectorRegistries[orientIdx];
        const meta = fragmentMeta[orientIdx];
        if (!meta || registry.size === 0)
            continue;
        const { nodeIdGrid, localBBox, orientation } = meta;
        const { dx, dy } = orientation;
        const gridCols = nodeIdGrid.length;
        const gridRows = nodeIdGrid[0]?.length ?? 0;
        for (const cv of registry.values()) {
            const [wx, wy] = cv.worldPos;
            const [lx, ly] = worldToLocal(wx, wy, dx, dy);
            if (cv.elevM === pipeElevationsM.yDirection) {
                // Y-node: connector lies on a column line → splice into a vertical edge.
                const col = Math.round((lx - localBBox.minX) / gridStepM);
                const rowFrac = (ly - localBBox.minY) / gridStepM;
                const rowA = Math.floor(rowFrac);
                const rowB = rowA + 1;
                if (col < 0 || col >= gridCols || rowA < 0 || rowB >= gridRows)
                    continue;
                if (!nodeIdGrid[col]?.[rowA]?.y || !nodeIdGrid[col]?.[rowB]?.y)
                    continue;
                const edgeId = `ge-v-o${orientIdx}-${col}-${rowA}`;
                if (!spliceMap.has(edgeId))
                    spliceMap.set(edgeId, []);
                spliceMap.get(edgeId).push({ connId: cv.id, t: rowFrac - rowA });
            }
            else {
                // X-node: connector lies on a row line → splice into a horizontal edge.
                const row = Math.round((ly - localBBox.minY) / gridStepM);
                const colFrac = (lx - localBBox.minX) / gridStepM;
                const colA = Math.floor(colFrac);
                const colB = colA + 1;
                if (row < 0 || row >= gridRows || colA < 0 || colB >= gridCols)
                    continue;
                if (!nodeIdGrid[colA]?.[row]?.x || !nodeIdGrid[colB]?.[row]?.x)
                    continue;
                const edgeId = `ge-h-o${orientIdx}-${colA}-${row}`;
                if (!spliceMap.has(edgeId))
                    spliceMap.set(edgeId, []);
                spliceMap.get(edgeId).push({ connId: cv.id, t: colFrac - colA });
            }
        }
    }
    if (spliceMap.size === 0)
        return allEdges;
    const edgesToRemove = new Set(spliceMap.keys());
    const newEdges = [];
    let seq = 0;
    // Build original-edge → replacement-chain for each spliced edge.
    const edgeById = new Map(allEdges.map((e) => [e.id, e]));
    for (const [edgeId, splices] of spliceMap) {
        const orig = edgeById.get(edgeId);
        if (!orig)
            continue;
        splices.sort((a, b) => a.t - b.t);
        const chain = [orig.from, ...splices.map((s) => s.connId), orig.to];
        for (let i = 0; i < chain.length - 1; i++) {
            newEdges.push({
                id: `gs-${seq++}`,
                from: chain[i],
                to: chain[i + 1],
                compCode: "GRID-SPLICE",
                profile: "CIRCULAR",
                size: { diameterM: 0.1 },
            });
        }
    }
    return [...allEdges.filter((e) => !edgesToRemove.has(e.id)), ...newEdges];
}
// ---------------------------------------------------------------------------
// Geometry utilities
// ---------------------------------------------------------------------------
/**
 * Projects a world-space point onto the local coordinate frame defined by the
 * orientation unit vector (dx, dy).
 *   local x-axis: (dx,  dy)
 *   local y-axis: (-dy, dx)
 */
function worldToLocal(wx, wy, dx, dy) {
    return [wx * dx + wy * dy, -wx * dy + wy * dx];
}
/**
 * Reconstructs a world-space point from local coordinates (lx, ly) and the
 * same orientation axes used in worldToLocal.
 */
function localToWorld(lx, ly, dx, dy) {
    return [lx * dx - ly * dy, lx * dy + ly * dx];
}
function polygonBBox(polygon) {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const [x, y] of polygon) {
        if (x < minX)
            minX = x;
        if (x > maxX)
            maxX = x;
        if (y < minY)
            minY = y;
        if (y > maxY)
            maxY = y;
    }
    return { minX, maxX, minY, maxY };
}
function unionBBox(bboxes) {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const b of bboxes) {
        if (b.minX < minX)
            minX = b.minX;
        if (b.maxX > maxX)
            maxX = b.maxX;
        if (b.minY < minY)
            minY = b.minY;
        if (b.maxY > maxY)
            maxY = b.maxY;
    }
    return { minX, maxX, minY, maxY };
}
/**
 * Finds where a trace line crosses the grid lines of another orientation.
 *
 * The trace line is defined as P(t) = startPoint + t * traceDir (t > 0).
 * The other orientation's grid has lines at every integer multiple of
 * GRID_STEP_M in its local coordinate frame:
 *   x-columns : lx2 = n * GRID_STEP_M  (lines parallel to (-dy2, dx2))
 *   y-rows    : ly2 = m * GRID_STEP_M  (lines parallel to  (dx2, dy2))
 *
 * Returns up to `maxCountPerAxis` intersections per axis, sorted by ascending
 * t, all within [0, maxDistance].
 *
 * Typical use: after a column scanline exits a room of orientation A, call
 * this with the exit point and column direction to find candidate bridge
 * nodes on orientation B's grid.
 */
export function findOrientationGridIntersections(startPoint, traceDir, otherOrientation, maxDistance, maxCountPerAxis = 3, 
/** Origin of B's local grid in B's local frame (localBBox.minX / minY). */
gridOrigin = { x: 0, y: 0 }, gridStepM = GRID_STEP_M) {
    const { dx: dx2, dy: dy2 } = otherOrientation;
    const [sx, sy] = startPoint;
    const [tx, ty] = traceDir;
    // Project start point and trace direction into the other orientation's local frame.
    const lx2Start = sx * dx2 + sy * dy2; // local-X of start in B's frame
    const ly2Start = -sx * dy2 + sy * dx2; // local-Y of start in B's frame
    const vx2 = tx * dx2 + ty * dy2; // d(local-X)/dt
    const vy2 = -tx * dy2 + ty * dx2; // d(local-Y)/dt
    const result = [];
    /**
     * Enumerate intersections with B's grid lines along one axis.
     * Grid lines sit at gridOriginOffset + n * GRID_STEP_M for integer n.
     * `localStart` is the projection of startPoint; `velocity` is d(localCoord)/dt.
     */
    const collect = (localStart, velocity, axis) => {
        if (Math.abs(velocity) < 1e-9)
            return; // trace runs parallel — no crossings
        const originOffset = axis === "x" ? gridOrigin.x : gridOrigin.y;
        // Shift into grid-relative coordinates.
        const relStart = localStart - originOffset;
        // First grid-line index strictly ahead of startPoint in the travel direction.
        const nStart = velocity > 0
            ? Math.floor(relStart / gridStepM) + 1
            : Math.ceil(relStart / gridStepM) - 1;
        const step = velocity > 0 ? 1 : -1;
        let count = 0;
        for (let n = nStart; count < maxCountPerAxis; n += step) {
            const t = (n * gridStepM - relStart) / velocity; // t ≥ 0 by construction
            if (t > maxDistance)
                break;
            if (t > 1e-9) {
                result.push({ point: [sx + t * tx, sy + t * ty], t, axis });
                count++;
            }
        }
    };
    collect(lx2Start, vx2, "x");
    collect(ly2Start, vy2, "y");
    result.sort((a, b) => a.t - b.t);
    return result;
}
